import { type ReactNode, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { UI_DATE_FORMAT_WEEK_DAY } from '../../constants';
import { parsePx } from '../../styles/newStyles/muiUtils';
import type { TimelinePlot, TimelineTick } from './timelineTypes';
import {
  TIMELINE_ICON_SIZE_PX,
  getTimelineTicks,
  timeToPlotX,
} from './timelineUtils';
import { useTimelinePlotWidth } from './useTimelinePlotWidth';
import LelyButton from '../muiWrappers/LelyButton/LelyButton';

export type TimelineMuiProps = {
  startMs: number;
  endMs: number;
  timeZone: string;
  formatTick: (timeMs: number, format: string) => string;
  onShift: (direction: -1 | 1) => void;
  shiftForwardDisabled?: boolean;
  shiftBackwardDisabled?: boolean;
  /** Height of the marker strip above the axis. */
  markerHeight?: number;
  /** Override tick generation (e.g. Vector zoom scale). Defaults to `getTimelineTicks`. */
  getTicks?: (plot: TimelinePlot, timeZone: string) => TimelineTick[];
  startLabel?: string;
  endLabel?: string;
  testId?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode | ((plot: TimelinePlot) => ReactNode);
};

/**
 * Shared MUI time-axis shell: carets, farm-local ticks, and a marker strip.
 * Page-specific markers (alarms, updates, KPI points) go in `children`.
 */
function TimelineMui({
  startMs,
  endMs,
  timeZone,
  formatTick,
  onShift,
  shiftForwardDisabled = false,
  shiftBackwardDisabled = false,
  markerHeight = TIMELINE_ICON_SIZE_PX,
  getTicks,
  startLabel,
  endLabel,
  testId = 'timeline-mui',
  sx,
  children,
}: TimelineMuiProps): JSX.Element {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const plotWidth = useTimelinePlotWidth(plotRef);

  const ticks = useMemo(() => {
    if (plotWidth <= 0) {
      return [];
    }
    if (getTicks) {
      return getTicks({ width: plotWidth, startMs, endMs }, timeZone);
    }
    return getTimelineTicks(startMs, endMs, plotWidth, timeZone);
  }, [endMs, getTicks, plotWidth, startMs, timeZone]);

  const plot: TimelinePlot = { width: plotWidth, startMs, endMs };
  const markers = typeof children === 'function' ? children(plot) : children;

  const resolvedStartLabel =
    startLabel ?? formatTick(startMs, UI_DATE_FORMAT_WEEK_DAY);
  const resolvedEndLabel =
    endLabel ?? formatTick(endMs, UI_DATE_FORMAT_WEEK_DAY);

  return (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'center',
          px: theme.lelySpacing.SM,
          py: theme.lelySpacing.SM,
        }),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      <LelyButton
        data-testid="left-caret-button"
        variant="tertiary"
        dense
        square
        leadingIcon="lely-icon-caret-left"
        aria-label="Previous time range"
        onClick={() => onShift(-1)}
        disabled={shiftBackwardDisabled}
      />

      <Box
        ref={plotRef}
        sx={(theme) => ({
          position: 'relative',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.lelySpacing.XS,
          overflow: 'hidden',
        })}
      >
        <Box
          sx={{
            position: 'relative',
            height: markerHeight,
            overflow: 'visible',
          }}
        >
          <Typography
            variant="caption"
            sx={(theme) => ({
              position: 'absolute',
              left: 4,
              top: 12,
              fontFamily: theme.lelyFontFamily.NOTO,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              lineHeight: `${markerHeight}px`,
            })}
          >
            {resolvedStartLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={(theme) => ({
              position: 'absolute',
              right: 4,
              top: 12,
              fontFamily: theme.lelyFontFamily.NOTO,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              lineHeight: `${markerHeight}px`,
            })}
          >
            {resolvedEndLabel}
          </Typography>
          {plotWidth > 0 ? markers : null}
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: 36,
          }}
        >
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: 0,
              right: 0,
              top: theme.lelySpacing.SM,
              borderRadius: '50%',
              height: '1px',
              bgcolor: 'border.subtle',
            })}
          />
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: 0,
              top: -3,
              width: 2,
              borderRadius: '50%',
              height: theme.lelySpacing._2XL,
              bgcolor: 'border.default',
            })}
          />
          <Box
            sx={(theme) => ({
              position: 'absolute',
              right: 0,
              top: -3,
              width: 2,
              height: theme.lelySpacing._2XL,
              bgcolor: 'border.default',
            })}
          />
          {ticks.map((tick) => {
            const x = timeToPlotX(tick.timeMs, startMs, endMs, plotWidth);
            const isAtStart = x <= 0.5;
            const showBelowLabel = !(isAtStart && tick.kind === 'date');

            return (
              <Box
                key={`${tick.kind}-${tick.timeMs}`}
                sx={{
                  position: 'absolute',
                  left: x,
                  top: 0,
                  width: 0,
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    left: 0,
                    top: -3,
                    borderRadius: '50%',
                    width: '2px',
                    height: theme.lelySpacing._2XL,
                    bgcolor: 'border.subtle',
                  })}
                />
                {showBelowLabel ? (
                  <Typography
                    variant="caption"
                    sx={(theme) => {
                      const gap = parsePx(theme.lelySpacing.XS, 4);

                      return {
                        position: 'absolute',
                        top: 12,
                        left: gap,
                        textAlign: 'left',
                        fontFamily: theme.lelyFontFamily.NOTO,
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                        p: 0,
                      };
                    }}
                  >
                    {formatTick(tick.timeMs, tick.format)}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>

      <LelyButton
        data-testid="right-caret-button"
        variant="tertiary"
        dense
        square
        leadingIcon="lely-icon-caret-right"
        aria-label="Next time range"
        onClick={() => onShift(1)}
        disabled={shiftForwardDisabled}
      />
    </Paper>
  );
}

export default TimelineMui;
